/**
 * "Management" tab — identity, Microsoft Entra ID login, backup, site recovery (shown
 * disabled: not supported yet) and guest OS updates (patch orchestration, periodic
 * assessment; hotpatch is not applicable to this form).
 */
import { NumberField, RadioField, ReadOnlyField, SelectField, TextField } from './FormFields'
import type { AppriseBackupFrequency, AppriseCreateOrExisting, ApprisePatchMode } from '@/types'
import { PATCH_MODE_OPTIONS } from './appriseCatalog'
import { CheckField, DisabledCheck } from './LiveSelect'
import { Banner } from './AppriseFields'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'

function BackupFields({ form }: { form: AppriseVmFormApi }) {
  const st = form.config.storage
  const bp = st.backup_policy
  const err = (k: string) => form.validation.errors[k]
  return (
    <>
      <CheckField label="Enable backup"
        description={form.config.server.server_environment === 'production' ? 'On by default for production servers' : 'Azure Backup — off unless the catalog or you turn it on'}
        checked={st.backup_required} onChange={(v) => form.setStorage({ backup_required: v })} />
      {form.validation.warnings['storage.backup_required'] && <Banner tone="warning">{form.validation.warnings['storage.backup_required']}</Banner>}
      {st.backup_required && (
        <>
          <SelectField<AppriseBackupFrequency> label="Backup frequency" required value={bp.frequency} error={err('storage.backup_policy.frequency')}
            options={form.options.backup_frequencies.map((f) => ({ value: f, label: f === 'daily' ? 'Daily' : 'Weekly' }))}
            onChange={(v) => form.setBackupPolicy({ frequency: v })} />
          <TextField label="Backup time (24h)" required mono value={bp.time} placeholder="23:00" error={err('storage.backup_policy.time')}
            onChange={(v) => form.setBackupPolicy({ time: v })} />
          <NumberField label="Retention (days)" required value={bp.retention_days} min={7} max={9999}
            error={err('storage.backup_policy.retention_days')} onChange={(v) => form.setBackupPolicy({ retention_days: v })} />
          <RadioField<AppriseCreateOrExisting> name="ap_vault_mode" label="Recovery Services vault" value={bp.vault_mode}
            options={[{ value: 'create_new', label: 'Create New' }, { value: 'existing', label: 'Existing' }]}
            onChange={(v) => form.setBackupPolicy({ vault_mode: v })} />
          <TextField label="Vault name" required mono value={bp.vault_name} error={err('storage.backup_policy.vault_name')}
            onChange={(v) => form.setBackupPolicy({ vault_name: v })} />
          {bp.vault_mode === 'existing' && (
            <TextField label="Vault resource group" required mono value={bp.vault_resource_group}
              error={err('storage.backup_policy.vault_resource_group')} onChange={(v) => form.setBackupPolicy({ vault_resource_group: v })} />
          )}
        </>
      )}
    </>
  )
}

export function AppriseManagementTab({ form }: { form: AppriseVmFormApi }) {
  const sec = form.config.security
  const o = form.config.operations
  const aad = form.config.server.aad_login_enabled
  const identityLocked = !form.catalog.overrides_allowed
  const userAssigned = sec.identity_mode === 'user_assigned'
  const patch = PATCH_MODE_OPTIONS.find((p) => p.id === o.patch_mode)
  return (
    <>
      <div className={styles.sectionTitle}>Identity</div>
      {userAssigned ? (
        <ReadOnlyField label="Managed identity" value="User-assigned" hint="Set by the platform catalog" />
      ) : (
        <CheckField label="Enable system assigned managed identity" checked={sec.identity_mode === 'system_assigned'} disabled={identityLocked}
          description={identityLocked ? 'Fixed by the catalog for this SKU' : 'Lets the VM authenticate to Azure services without credentials'}
          error={form.validation.errors['security.identity_mode']}
          onChange={(v) => {
            form.setSecurity({ identity_mode: v ? 'system_assigned' : 'none' })
            if (!v && aad) form.setServer({ aad_login_enabled: false })
          }} />
      )}
      <CheckField label="Login with Microsoft Entra ID" checked={aad} disabled={sec.identity_mode !== 'system_assigned'}
        description="Requires the system assigned managed identity" onChange={(v) => form.setServer({ aad_login_enabled: v })} />

      <div className={styles.sectionTitle}>Backup</div>
      <BackupFields form={form} />

      <div className={styles.sectionTitle}>Disaster recovery</div>
      <DisabledCheck label="Enable Site Recovery" />

      <div className={styles.sectionTitle}>Guest OS updates</div>
      <SelectField<ApprisePatchMode> label="Patch orchestration options" value={o.patch_mode} error={form.validation.errors['operations.patch_mode']}
        options={PATCH_MODE_OPTIONS.map((p) => ({ value: p.id, label: p.label }))} hint={patch?.hint}
        onChange={(v) => form.setOperations({ patch_mode: v })} />
      <CheckField label="Periodic assessment" checked={o.periodic_assessment} full={false}
        description="Azure checks for missing updates every 24 hours" onChange={(v) => form.setOperations({ periodic_assessment: v })} />
      <ReadOnlyField label="Enable hotpatch" value="Not applicable" hint="Hotpatching needs Azure-orchestrated patching on a supported image — it is not offered by this form" />
    </>
  )
}
